/* ==========================================================================
   PIELLE 2.0 · assets/pielle.js · vanilla, sin dependencias, ~5 KB
   Comportamientos compartidos por las sections Pielle:
     · reveal        [.pl-reveal]         un solo IntersectionObserver
     · acordeón      [details.pl-acc]     anima la altura (sin JS ya abre/cierra)
     · tabs          [data-pl-tabs]       aria-selected + teclado (← → Home End)
     · slider        [data-pl-slider]     flechas, puntos, contador 01 / 03
   Todo se inicializa por raíz para que el editor de Shopify (shopify:section:load)
   pueda re-inicializar solo la sección que cambió.
   ========================================================================== */
(function () {
  'use strict';
  var RM = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------- reveal ---------- */
  var revealIO = ('IntersectionObserver' in window) ? new IntersectionObserver(function (entries) {
    entries.forEach(function (e) {
      if (e.isIntersecting) { e.target.classList.add('is-in'); revealIO.unobserve(e.target); }
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 }) : null;

  function initReveal(root) {
    var els = root.querySelectorAll('.pl-reveal:not(.is-in)');
    if (!revealIO || RM) { els.forEach(function (el) { el.classList.add('is-in'); }); return; }
    els.forEach(function (el, i) {
      if (!el.style.getPropertyValue('--pl-delay') && el.dataset.plStagger !== undefined) {
        el.style.setProperty('--pl-delay', Math.min(i * 60, 360) + 'ms');
      }
      revealIO.observe(el);
    });
  }

  /* ---------- acordeón ---------- */
  function initAccordion(root) {
    root.querySelectorAll('details.pl-acc').forEach(function (d) {
      if (d.dataset.plReady) return;
      d.dataset.plReady = '1';
      var sum = d.querySelector('summary');
      var body = d.querySelector('.pl-acc__a');
      if (!sum || !body) return;
      // envolver el contenido para poder animar height
      var wrap = document.createElement('div');
      wrap.className = 'pl-acc__wrap';
      body.parentNode.insertBefore(wrap, body);
      wrap.appendChild(body);
      var group = d.closest('[data-pl-acc-single]');

      sum.addEventListener('click', function (ev) {
        ev.preventDefault();
        if (d.classList.contains('is-animating')) return;
        if (d.open) { close(d, wrap); }
        else {
          if (group) group.querySelectorAll('details.pl-acc[open]').forEach(function (o) { if (o !== d) close(o, o.querySelector('.pl-acc__wrap')); });
          open(d, wrap);
        }
      });
    });
  }
  function open(d, wrap) {
    if (RM) { d.open = true; return; }
    d.classList.add('is-animating');
    wrap.style.height = '0px';
    d.open = true;
    var h = wrap.scrollHeight;
    requestAnimationFrame(function () { wrap.style.height = h + 'px'; });
    once(wrap, function () { wrap.style.height = ''; d.classList.remove('is-animating'); });
  }
  function close(d, wrap) {
    if (RM || !wrap) { d.open = false; return; }
    d.classList.add('is-animating');
    wrap.style.height = wrap.scrollHeight + 'px';
    requestAnimationFrame(function () { wrap.style.height = '0px'; });
    once(wrap, function () { d.open = false; wrap.style.height = ''; d.classList.remove('is-animating'); });
  }
  function once(el, fn) {
    var done = false;
    function h() { if (done) return; done = true; el.removeEventListener('transitionend', h); fn(); }
    el.addEventListener('transitionend', h);
    setTimeout(h, 420); // red de seguridad si no dispara transitionend
  }

  /* ---------- tabs ---------- */
  function initTabs(root) {
    root.querySelectorAll('[data-pl-tabs]').forEach(function (box) {
      if (box.dataset.plReady) return;
      box.dataset.plReady = '1';
      var tabs = Array.prototype.slice.call(box.querySelectorAll('[role="tab"]'));
      if (!tabs.length) return;
      function activate(tab, focus) {
        tabs.forEach(function (t) {
          var on = t === tab;
          t.setAttribute('aria-selected', on ? 'true' : 'false');
          t.tabIndex = on ? 0 : -1;
          var p = t.getAttribute('aria-controls') && box.querySelector('#' + t.getAttribute('aria-controls'));
          if (p) { p.hidden = !on; }
        });
        if (focus) tab.focus();
        // que la pestaña activa quede visible en la fila con scroll (mobile)
        if (tab.scrollIntoView) { try { tab.scrollIntoView({ block: 'nearest', inline: 'center', behavior: RM ? 'auto' : 'smooth' }); } catch (e) {} }
        box.dispatchEvent(new CustomEvent('pl:tab', { detail: { tab: tab }, bubbles: true }));
      }
      tabs.forEach(function (t, i) {
        t.addEventListener('click', function () { activate(t); });
        t.addEventListener('keydown', function (ev) {
          var k = ev.key, n = null;
          if (k === 'ArrowRight' || k === 'ArrowDown') n = tabs[(i + 1) % tabs.length];
          else if (k === 'ArrowLeft' || k === 'ArrowUp') n = tabs[(i - 1 + tabs.length) % tabs.length];
          else if (k === 'Home') n = tabs[0];
          else if (k === 'End') n = tabs[tabs.length - 1];
          if (n) { ev.preventDefault(); activate(n, true); }
        });
      });
      var initial = tabs.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0] || tabs[0];
      activate(initial);
    });
  }

  /* ---------- slider ---------- */
  function initSlider(root) {
    root.querySelectorAll('[data-pl-slider]').forEach(function (s) {
      if (s.dataset.plReady) return;
      s.dataset.plReady = '1';
      var track = s.querySelector('.pl-slider__track');
      var items = track ? Array.prototype.slice.call(track.children) : [];
      if (!track || items.length < 2) return;
      var prev = s.querySelector('[data-pl-prev]');
      var next = s.querySelector('[data-pl-next]');
      var dotsBox = s.querySelector('.pl-slider__dots');
      var count = s.querySelector('.pl-slider__count');
      var dots = [];
      if (dotsBox) {
        items.forEach(function (_, i) {
          var b = document.createElement('button');
          b.type = 'button'; b.className = 'pl-slider__dot';
          b.setAttribute('aria-label', 'Ir a ' + (i + 1));
          b.addEventListener('click', function () { go(i); });
          dotsBox.appendChild(b); dots.push(b);
        });
      }
      var idx = 0;
      function pad(n) { return (n < 10 ? '0' : '') + n; }
      function paint() {
        dots.forEach(function (d, i) { d.classList.toggle('is-active', i === idx); });
        if (count) count.innerHTML = '<b>' + pad(idx + 1) + '</b> / ' + pad(items.length);
        if (prev) prev.disabled = idx === 0;
        if (next) next.disabled = idx >= items.length - 1;
        s.style.setProperty('--pl-progress', ((idx + 1) / items.length * 100) + '%');
        s.dispatchEvent(new CustomEvent('pl:slide', { detail: { index: idx }, bubbles: true }));
      }
      function spad() {
        var cs = getComputedStyle(track);
        return parseFloat(cs.scrollPaddingInlineStart) || parseFloat(cs.scrollPaddingLeft) || 0;
      }
      function pos(i) { return items[i].offsetLeft - track.offsetLeft - spad(); }
      function go(i) {
        i = Math.max(0, Math.min(items.length - 1, i));
        var x = pos(i);
        try { track.scrollTo({ left: x, behavior: RM ? 'auto' : 'smooth' }); } catch (e) { track.scrollLeft = x; }
      }
      if (prev) prev.addEventListener('click', function () { go(idx - 1); });
      if (next) next.addEventListener('click', function () { go(idx + 1); });
      // índice según scroll (throttle por tiempo, sin observers duplicados)
      var tm = null;
      track.addEventListener('scroll', function () {
        if (tm) return;
        tm = setTimeout(function () {
          tm = null;
          var best = 0, bd = Infinity, sl = track.scrollLeft;
          items.forEach(function (it, i) {
            var d = Math.abs(pos(i) - sl);
            if (d < bd) { bd = d; best = i; }
          });
          if (best !== idx) { idx = best; paint(); }
        }, 60);
      }, { passive: true });
      track.addEventListener('keydown', function (ev) {
        if (ev.key === 'ArrowRight') { ev.preventDefault(); go(idx + 1); }
        if (ev.key === 'ArrowLeft') { ev.preventDefault(); go(idx - 1); }
      });
      if (!track.hasAttribute('tabindex')) track.tabIndex = 0;
      paint();
      s.plGo = go;
    });
  }

  /* ---------- ticker (barra rotativa) ---------- */
  function initTicker(root) {
    root.querySelectorAll('[data-pl-ticker]').forEach(function (t) {
      var track = t.querySelector('.tk__track');
      var group = t.querySelector('.tk__group');
      if (!track || !group) return;
      function size() {
        var w = group.getBoundingClientRect().width;
        var speed = parseFloat(t.dataset.speed) || 60;   // px por segundo
        if (w > 0) t.style.setProperty('--tk-dur', (w / speed).toFixed(2) + 's');
      }
      size();
      if (document.fonts && document.fonts.ready) document.fonts.ready.then(size);
      if (!t.dataset.plReady) {
        t.dataset.plReady = '1';
        var rt = null;
        window.addEventListener('resize', function () { clearTimeout(rt); rt = setTimeout(size, 120); }, { passive: true });
      }
    });
  }

  /* ---------- init ---------- */
  function init(root) {
    root = root || document;
    initReveal(root); initAccordion(root); initTabs(root); initSlider(root); initTicker(root);
  }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { init(); });
  else init();
  if (window.Shopify && window.Shopify.designMode) {
    document.addEventListener('shopify:section:load', function (e) { init(e.target); });
  }
  window.Pielle = window.Pielle || {};
  window.Pielle.init = init;
})();
